// Procedural block textures, voxel terrain rendering, chunk streaming,
// and the village/wreck siting+streaming that rides along with it.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Another subsystem with no assigned step (see world/weather.ts's header
// for why it gets its own file instead of living in main.ts). This one
// absorbs the most: legacy's "Voxel terrain", "Column streaming", the
// rendering halves of "Villages" and "Shipwrecks", and the water-habitat
// decorations — all of it is chunk-adjacent world generation.

import * as THREE from 'three';
import {
  HEX_R, HEX_H, PROP_SCALE, SEA_LEVEL, GEN_DEPTH, PEAK_LEVEL, HEX_SIDE_DIRS, hexToWorld, worldToHex, axialDistance,
  terrainHeightAt, valueNoise2D, hash2, getTerrainSeed,
} from '@world/terrain';
import {
  createWaterSurfaceMesh, habitatAt, makeRippleRings, makeRippleGeometry, rippleMat, swellAt,
} from '@world/water';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { buildHexPrism } from '@world/hexPrism';
import { buildGrassClumpGeometry, grassMat } from '@world/grass';
import { toonMat, toonVertexColorMat, addOutlines, OUTLINE_THICKNESS as OUTLINE_T } from '@world/toon';
import { S, washTile, toTexture, bloom, rgba } from '@world/wash';
import { spawnTree, removeTreeMesh } from '@entities/trees';
import { fishGeo, fishMats } from '@entities/fish';
import { player } from '@entities/player';
import { villages, extraVillages, isNearVillageSite, type Village } from '@economy/market';
import { hullGeo } from '@systems/building';
import type { WreckSite } from '@systems/diving';

const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

// ================= Procedural block textures =================
// The wash generator itself lives in world/wash.ts, shared with the water
// surface. What follows is only the per-terrain recipes.

// Lighter and warmer than the old saturated palette — watercolour gets its
// character from paper showing through, so nothing here is fully dense.
const PALETTE = {
  grass: { base: '#93b96f', pigs: ['#7fa65a', '#a8c886', '#729a52'], edge: '#5f8446', grain: '#4d6b39' },
  earth: { base: '#b39068', pigs: ['#9e7b52', '#c5a67c', '#8b6a47'], edge: '#7a5a3c', grain: '#5e4630' },
  stone: { base: '#aba69d', pigs: ['#969088', '#c0bbb3', '#878179'], edge: '#6f6a63', grain: '#57534d' },
  sand: { base: '#e7d9b0', pigs: ['#dccb9c', '#f0e5c6', '#d2bd88'], edge: '#b8a273', grain: '#8f8264' },
  // Seabed sand, for shelves standing out in the water rather than at a
  // shore. Only readsAsSea() hexes get it, so beaches keep their ochre.
  wetSand: { base: '#cfd8c9', pigs: ['#c2cdbe', '#dde4d8', '#b3c0b2'], edge: '#9fae9f', grain: '#87968a' },
};

// Mottles for the terrain tops. Without these the washes are near-flat and
// the per-instance UV offset in tileMat has nothing to slide through — see
// WashOpts.mottle. They stay in family with their own palette: a sunlit
// stretch and a shaded one of the same ground, not a second material.
const MOTTLE = {
  grass: { colors: ['#b7c97f', '#6b8f4c'], count: 3, alpha: 0.5 },
  sand: { colors: ['#f2e9c8', '#c9b183'], count: 3, alpha: 0.42 },
  stone: { colors: ['#c3bfb6', '#867f76'], count: 3, alpha: 0.45 },
  earth: { colors: ['#c9ab82', '#8c6b48'], count: 3, alpha: 0.4 },
};
// Blurred less than the default: the mottle is the structure the UV slide
// reveals, and 6px of blur on a 128px tile softens most of it away again.
const TOP_BLUR = 3.5;

function texGrassTop(): THREE.CanvasTexture {
  const p = PALETTE.grass;
  return toTexture(washTile(p.base, p.pigs, p.edge, p.grain,
    { blooms: 11, alpha: [0.3, 0.6], mottle: MOTTLE.grass }), true, TOP_BLUR);
}
function texGrassSide(): THREE.CanvasTexture {
  const e = PALETTE.earth, g = PALETTE.grass;
  const c = washTile(e.base, e.pigs, e.edge, e.grain,
    { blooms: 10, alpha: [0.28, 0.55], mottle: MOTTLE.earth });
  const ctx = c.getContext('2d')!;
  // Grass cap along the top of the wall, fading into the earth rather than
  // meeting it at a ruled line, then a few blooms to ragged the boundary.
  const capH = Math.round(S * 0.34);
  const grad = ctx.createLinearGradient(0, 0, 0, capH);
  grad.addColorStop(0, rgba(g.base, 1));
  grad.addColorStop(0.72, rgba(g.base, 0.9));
  grad.addColorStop(1, rgba(g.edge, 0));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, S, capH);
  for (let i = 0; i < 5; i++) {
    bloom(ctx, g.pigs[i % g.pigs.length], g.edge, Math.random() * S, capH * (0.35 + Math.random() * 0.8), S * 0.1, 0.4);
  }
  return toTexture(c, false);
}
function texDirt(): THREE.CanvasTexture {
  const p = PALETTE.earth;
  return toTexture(washTile(p.base, p.pigs, p.edge, p.grain,
    { blooms: 10, alpha: [0.28, 0.55], mottle: MOTTLE.earth }), false, TOP_BLUR);
}
function texStone(): THREE.CanvasTexture {
  const p = PALETTE.stone;
  return toTexture(washTile(p.base, p.pigs, p.edge, p.grain,
    { blooms: 11, alpha: [0.3, 0.58], mottle: MOTTLE.stone }), true, TOP_BLUR);
}
function texSand(): THREE.CanvasTexture {
  const p = PALETTE.sand;
  return toTexture(washTile(p.base, p.pigs, p.edge, p.grain,
    { blooms: 10, alpha: [0.26, 0.5], mottle: MOTTLE.sand }), true, TOP_BLUR);
}
function texWetSand(): THREE.CanvasTexture {
  const p = PALETTE.wetSand;
  return toTexture(washTile(p.base, p.pigs, p.edge, p.grain));
}

// Kawara pantiles: rounded ridges running down the slope, each catching a
// highlight on one side and dropping a shadow on the other, crossed by the
// course lines. Blurred far less than the terrain washes — these lines are
// drawn detail and should stay legible rather than bleeding away.
function texRoofTile(): THREE.CanvasTexture {
  const c = washTile('#4d4c50', ['#44434b', '#5a5a64', '#3c3b41'], '#33323a', '#2b2a30');
  const ctx = c.getContext('2d')!;
  const cols = 7;
  for (let i = 0; i < cols; i++) {
    const x = (i + 0.5) * (S / cols);
    ctx.fillStyle = 'rgba(150,152,163,0.5)';
    ctx.fillRect(x - 2.5, 0, 3, S);
    ctx.fillStyle = 'rgba(26,26,32,0.45)';
    ctx.fillRect(x + 1.5, 0, 2, S);
  }
  ctx.fillStyle = 'rgba(22,22,28,0.3)';
  for (let i = 1; i < 5; i++) ctx.fillRect(0, i * (S / 5), S, 1.5);
  const tex = toTexture(c, true, 1.6);
  tex.repeat.set(3, 2);
  return tex;
}

// Thatch: vertical straw streaking with a darker, heavier cut edge along
// the bottom course where the bundle ends are exposed.
function texThatch(): THREE.CanvasTexture {
  const c = washTile('#a8925e', ['#9a8454', '#b8a271', '#8d784b'], '#7c6940', '#6b5a37');
  const ctx = c.getContext('2d')!;
  // Streaks are kept clear of the bottom edge so they don't get clipped —
  // the tile repeats twice up the slope, and anything cut off at the edge
  // would show as a false course line across the middle of the roof.
  for (let i = 0; i < S * 1.6; i++) {
    ctx.fillStyle = ['rgba(120,102,64,0.45)', 'rgba(198,180,138,0.4)'][Math.floor(Math.random() * 2)];
    const len = 5 + Math.random() * 9;
    ctx.fillRect(Math.random() * S, Math.random() * (S - len), 1, len);
  }
  const tex = toTexture(c, true, 2.2);
  tex.repeat.set(3, 2);
  return tex;
}

// One wash per terrain type. The per-hex difference comes from instanceColor
// and instanceUvOffset rather than from several baked variants — a tint and
// a UV slide cost nothing per block, where a variant cost a whole extra
// texture and still repeated.
// vertexColors is on so that instanceColor reaches the fragment stage.
//
// The UV slide is what stops the terrain reading as one stamped tile. Every
// prism shares a single geometry, and ExtrudeGeometry gives the caps the
// shape's own x/y as their UVs, so without this every hex of a type shows
// the *identical* crop of the identical wash, and a six-block column repeats
// the same wall texture all the way up. The washes are seamless under
// RepeatWrapping, so shifting each instance's sample point by a hashed
// amount gives every hex its own patch of paper for the price of one
// attribute — no atlas, no extra texture memory.
//
// `axes` is 'xy' wherever the wash can slide freely, and 'x' for the grass
// side wall, whose grass cap is baked against the top edge of the tile:
// sliding v there would drag the green band down the middle of the cliff.
function tileMat(tex: THREE.Texture, axes: 'xy' | 'x' = 'xy'): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ map: tex, vertexColors: true });
  const off = axes === 'xy' ? 'instanceUvOffset' : 'vec2( instanceUvOffset.x, 0.0 )';
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 instanceUvOffset;')
      .replace('#include <uv_vertex>', `#include <uv_vertex>\n\tvMapUv += ${off};`);
  };
  // Without a key of its own, three would hand this material a program
  // compiled for an unpatched one (or vice versa) out of its cache.
  m.customProgramCacheKey = () => 'tileMat-' + axes;
  return m;
}
function uniformSet(tex: THREE.CanvasTexture): THREE.Material[] {
  const m = tileMat(tex);
  return [m, m];
}

// Extruded prisms use two material slots: [caps (top/bottom), side walls]
const MATERIAL_SETS: Record<number, THREE.Material[]> = {
  0: [tileMat(texGrassTop()), tileMat(texGrassSide(), 'x')],
  1: uniformSet(texDirt()),
  2: uniformSet(texStone()),
  5: uniformSet(texSand()),
  6: uniformSet(texWetSand()), // seabed sand — see readsAsSea()
};

const geometry = buildHexPrism(HEX_R, HEX_H);
const edgesGeo = new THREE.EdgesGeometry(geometry, 28);

// In the reference art a hex top is bounded by a pale seam — unpainted
// paper between two washes — while the rocky sides keep a dark ink line.
// Both are baked into one geometry as vertex colours so a block still
// costs a single LineSegments, rather than one per colour.
const SEAM_LAND = new THREE.Color(0xf6efdd);
const SEAM_INK = new THREE.Color(0x5a4f42);
// The same seam in a cool tone, for hexes that sit out in the water. Only
// the line colour changes — the terrain palette underneath is untouched,
// so beaches keep their sand.
const SEAM_WET = new THREE.Color(0xeaf7fa);
const SEAM_WET_INK = new THREE.Color(0x4a6b7a);
function buildSeamGeometry(src: THREE.BufferGeometry, topY: number, top: THREE.Color, side: THREE.Color): THREE.BufferGeometry {
  const pos = src.attributes.position;
  const colors: number[] = [];
  for (let i = 0; i < pos.count; i += 2) {
    const onTop = Math.abs(pos.getY(i) - topY) < 1e-4 && Math.abs(pos.getY(i + 1) - topY) < 1e-4;
    const c = onTop ? top : side;
    colors.push(c.r, c.g, c.b, c.r, c.g, c.b);
  }
  const g = src.clone();
  g.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return g;
}
const seamGeo = buildSeamGeometry(edgesGeo, HEX_H / 2, SEAM_LAND, SEAM_INK);
const seamGeoWet = buildSeamGeometry(edgesGeo, HEX_H / 2, SEAM_WET, SEAM_WET_INK);
const seamMat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.7 });

// ================= Voxel terrain =================
// Land blocks are instanced: one pool per terrain type, so a loaded area of
// several thousand blocks costs a handful of draw calls instead of a mesh
// each. Water stays as individual meshes — it's transparent (so instancing
// would fight depth sorting) and the click-to-cast raycast identifies it by
// userData.isWater on the hit object.
type WorldCell =
  | { kind: 'block'; type: number; idx: number }
  | { kind: 'water'; mesh: THREE.Object3D };

const world = new Map<string, WorldCell>();
export const blockGroup = new THREE.Group();
// Seams live outside blockGroup on purpose: the cast raycast tests
// blockGroup's children non-recursively, and Line raycasting uses a
// generous threshold that would let a seam swallow clicks meant for water.
export const seamGroup = new THREE.Group();

function key(q: number, r: number, y: number): string {
  return q + ',' + r + ',' + y;
}

// Purely cosmetic settle applied to a whole column, so no hex sits at
// exactly the height of its neighbour. It is NOT fed back into
// terrainHeightAt: the logical height still drives standing height, water,
// jetties and the village's flat-footprint test, so gameplay is untouched
// and this stays a couple of centimetres of visual noise on a 0.33 block.
const HEX_SETTLE = HEX_H * 0.055;
function columnSettle(q: number, r: number): number {
  return (hash2(q * 419 + 31, r * 733 + 17) - 0.5) * 2 * HEX_SETTLE;
}

// Snap-rotate by whole sixths of a turn: the prism is six-fold symmetric,
// so neighbours still tile exactly, but the wash lands differently.
function blockRotY(q: number, r: number): number {
  return (Math.PI / 3) * Math.floor(hash2(q * 89 + 7, r * 149 + 41) * 6);
}

// Where in the wash this block samples from — see tileMat. Rotation alone
// only turns the same patch of paper six ways; this actually moves it.
// y is folded in so the blocks stacked in one column each get their own
// slide, which is what breaks the banding down a tall cliff face.
function blockUvOffsetU(q: number, r: number, y: number): number {
  return hash2(q * 613 + y * 29 + 19, r * 887 + y * 53 + 23);
}
function blockUvOffsetV(q: number, r: number, y: number): number {
  return hash2(q * 379 + y * 71 + 31, r * 457 + y * 37 + 7);
}

// Per-hex tint, on two scales as the reference does it: a slow noise field
// shading whole stretches lighter or darker, plus a little per-hex jitter on
// top. Kept close to white — it multiplies the wash, it doesn't replace it.
const blockTint = new THREE.Color();
function tintFor(q: number, r: number): THREE.Color {
  const { q: seedQ, r: seedR } = getTerrainSeed();
  const broad = valueNoise2D((q + seedQ + 2400) * 0.055, (r + seedR + 2600) * 0.055);
  const fine = hash2(q * 257 + 5, r * 601 + 23);
  // Wider than it was. The tint used to be doing all the per-hex work on
  // its own and had to stay subtle to avoid reading as flat shading; with
  // the wash now carrying real structure underneath it, whole stretches can
  // swing noticeably lighter or darker without the ground going plastic.
  const v = 0.84 + broad * 0.28 + (fine - 0.5) * 0.12;
  // A warm/cool swing along the same broad field, so a sunlit stretch runs
  // ochre and a sheltered one runs cool — value alone reads as shadow.
  const warm = (broad - 0.5) * 0.09;
  return blockTint.setRGB(v * (1 + warm), v, v * (1 - warm));
}

// Capacities are generous: a pool running dry means blocks silently stop
// appearing, which reads as holes in the ground. UNLOAD_RADIUS is wider
// than LOAD_RADIUS, so more columns can be resident than are being drawn.
// Seabed now claims three layers of every submerged column, not just the
// cap, so it needs the headroom the soil layer no longer uses.
const BLOCK_CAPACITY: Record<number, number> = { 0: 1800, 1: 2600, 2: 4200, 5: 1800, 6: 3000 };
// Populated further down, once InstancedPool has actually been declared —
// a class is in the temporal dead zone until its declaration is evaluated,
// so constructing one up here would throw at module load. The type
// annotation is fine: types are erased.
const blockPools: Record<number, InstancedPool> = {};

function spawnBlock(q: number, r: number, y: number, blockIndex: number): void {
  const k = key(q, r, y);
  if (world.has(k)) return;
  const pos = hexToWorld(q, r);
  const pool = blockPools[blockIndex];
  const idx = pool.place(
    pos.x, y * HEX_H + columnSettle(q, r), pos.z,
    blockRotY(q, r), 1, tintFor(q, r),
  );
  if (idx === null) return;
  pool.setUvOffset(idx, blockUvOffsetU(q, r, y), blockUvOffsetV(q, r, y));
  world.set(k, { kind: 'block', type: blockIndex, idx });
}

function addWaterSurface(q: number, r: number): void {
  const k = key(q, r, SEA_LEVEL);
  if (world.has(k)) return;
  const mesh = createWaterSurfaceMesh(q, r);
  blockGroup.add(mesh);
  waterMeshes.add(mesh);
  world.set(k, { kind: 'water', mesh });
}

// Water tiles are the one part of the terrain that still moves, so they're
// tracked separately rather than being fished back out of blockGroup —
// which now holds the instanced land pools too.
const waterMeshes = new Set<THREE.Mesh>();

export function updateWaterSwell(now: number): void {
  waterMeshes.forEach((m) => {
    m.position.y = WATER_SURF_Y + swellAt(m.position.x, m.position.z, now);
  });
}

// ================= Water habitat decorations =================
export const decoGroup = new THREE.Group();
// Okinawan loop-root mangroves run a fairly bright yellow-green, not the
// dark forest green a "swamp tree" suggests.
const mangLeafMats = [0x6d9a4e, 0x5c8a44, 0x7cab5c, 0x517a3e].map((c) => toonMat(c));
const mangRootMat = toonMat(0x4c4030);
// A Ryukyu reef palette: mostly the muted golds and tans of massive Porites
// and Acropora, with the blue coral Okinawa is known for and a purple
// branching tip as accents. Candy colours read as a cartoon aquarium.
const coralMats = [0xb08050, 0xc9a06a, 0x8fa9c6, 0xa88fc0, 0x8fb3a4, 0xc98d7a].map((c) => toonMat(c));
const decoBlobGeo = new THREE.IcosahedronGeometry(1, 0);
const rootGeo = new THREE.CylinderGeometry(0.015, 0.022, 1, 4);
const mangTrunkGeo = new THREE.CylinderGeometry(0.05, 0.08, 1, 5);
const coralBranchGeo = new THREE.CylinderGeometry(0.018, 0.035, 1, 5);

// Matches water.ts's WATER_SURF_Y formula (avoids importing it just for one constant).
const WATER_SURF_Y = SEA_LEVEL * HEX_H + HEX_H * 0.2;

// One mangrove. The silhouette that identifies these is a wide, tangled
// loop-root skirt — roughly twice the width of the canopy — under a squat
// rounded crown. Everything is driven off `seed` and `size` so no two are
// alike; an even ring of identical roots is exactly what read as fake.
function addMangrovePlant(g: THREE.Group, seed: number, cx: number, cz: number, size: number): void {
  const leafMat = mangLeafMats[Math.floor(seed * mangLeafMats.length) % mangLeafMats.length];

  const trunkH = 0.34 * size;
  const trunk = new THREE.Mesh(mangTrunkGeo, mangRootMat);
  trunk.scale.set(size, trunkH, size);
  trunk.position.set(cx, trunkH * 0.55, cz);
  g.add(trunk);

  // Roots: irregular angles, reaches and leans, on two height tiers so they
  // cross each other instead of sitting in one clean plane.
  const roots = 5 + Math.floor(((seed * 37) % 1) * 4);
  for (let i = 0; i < roots; i++) {
    const j = (seed * 71 + i * 29.7) % 1;
    const k = (seed * 133 + i * 17.3) % 1;
    const a = (i / roots) * Math.PI * 2 + (j - 0.5) * 1.5;
    const reach = (0.5 + k * 0.5) * size;
    const root = new THREE.Mesh(rootGeo, mangRootMat);
    root.scale.set(1.5 * size, (0.7 + k * 0.4) * size, 1.5 * size);
    root.position.set(cx + Math.cos(a) * reach * 0.5, (i % 2 ? 0.26 : 0.14) * size, cz + Math.sin(a) * reach * 0.5);
    // Signs matter here: rotating +z tips the cylinder's TOP toward +x, so
    // the foot swings the other way. These are set so the top stays high on
    // the trunk and the foot angles down and outward into the water —
    // reversed, they splay at the top and read as a funnel.
    const lean = 0.75 + j * 0.4;
    root.rotation.z = Math.cos(a) * lean;
    root.rotation.x = -Math.sin(a) * lean;
    g.add(root);
  }

  // Crown: squat, wider than tall, and narrower than the roots beneath it.
  const blobs = 2 + Math.floor(((seed * 53) % 1) * 2);
  for (let i = 0; i < blobs; i++) {
    const j = (seed * 91 + i * 41.7) % 1;
    const rad = (0.3 + j * 0.18) * size;
    const a = (i / blobs) * Math.PI * 2 + seed * 4;
    const off = i === 0 ? 0 : (0.14 + j * 0.14) * size;
    const c = new THREE.Mesh(decoBlobGeo, leafMat);
    c.scale.set(rad, rad * 0.66, rad);
    c.position.set(cx + Math.cos(a) * off, (0.6 + j * 0.16) * size, cz + Math.sin(a) * off);
    c.rotation.y = j * 6;
    g.add(c);
  }
}

function makeMangrove(q: number, r: number): THREE.Group {
  const g = new THREE.Group();
  const pos = hexToWorld(q, r);
  const v = hash2(q * 91 + 5, r * 57 + 9);
  const w = hash2(q * 29 + 3, r * 83 + 7);

  addMangrovePlant(g, v, 0, 0, 0.75 + v * 0.7);
  // Often a smaller one alongside: they grow in stands, and one identical
  // specimen per hex was most of what made a patch look stamped out.
  if (w > 0.55) {
    const a = w * Math.PI * 2;
    addMangrovePlant(g, w, Math.cos(a) * 0.72, Math.sin(a) * 0.72, 0.38 + w * 0.3);
  }

  g.position.set(pos.x, WATER_SURF_Y - 0.04 * PROP_SCALE, pos.z);
  g.rotation.y = v * Math.PI * 2;
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

// A reef of mixed forms — branching staghorn, low brain domes and fingered
// tubes — so it reads as coral rather than a row of identical lumps. The
// group now carries the seabed height and PROP_SCALE, so the clumps are
// authored in local space instead of absolute world coordinates.
function makeCoral(q: number, r: number, h: number): THREE.Group {
  const g = new THREE.Group();
  const pos = hexToWorld(q, r);
  const bedY = h * HEX_H + HEX_H / 2;
  const clumps = 3 + Math.floor(hash2(q * 17 + 1, r * 23 + 1) * 2);
  for (let i = 0; i < clumps; i++) {
    const v = hash2(q * 31 + i * 7, r * 43 + i * 11);
    const w = hash2(q * 61 + i * 19, r * 13 + i * 5);
    const mat = coralMats[Math.floor(v * coralMats.length) % coralMats.length];
    const cx = (v - 0.5) * 1.4, cz = (w - 0.5) * 1.4;
    const form = Math.floor(w * 4) % 4;
    if (form === 0) {
      // Staghorn Acropora: a thicket rather than a few spokes
      for (let b = 0; b < 6; b++) {
        const a = (b / 6) * Math.PI * 2 + v * 6;
        const len = 0.26 + ((v * (b + 2) * 13) % 1) * 0.32;
        const br = new THREE.Mesh(coralBranchGeo, mat);
        br.scale.set(0.9, len, 0.9);
        br.position.set(cx + Math.cos(a) * 0.1, len * 0.45, cz + Math.sin(a) * 0.1);
        br.rotation.z = -Math.cos(a) * 0.45;
        br.rotation.x = Math.sin(a) * 0.45;
        g.add(br);
      }
    } else if (form === 1) {
      // Massive Porites: a big rounded boulder, the reef's bulk
      const rad = 0.3 + v * 0.22;
      const dome = new THREE.Mesh(decoBlobGeo, mat);
      dome.scale.set(rad, rad * 0.72, rad);
      dome.position.set(cx, rad * 0.55, cz);
      dome.rotation.y = v * 5;
      g.add(dome);
    } else if (form === 2) {
      // Table Acropora: a flat plate on a short stem. The most recognisable
      // thing on an Okinawan reef, and the one that sells it as a reef
      // rather than a pile of rocks.
      const stemH = 0.14 + v * 0.1;
      const stem = new THREE.Mesh(coralBranchGeo, mat);
      stem.scale.set(1.6, stemH, 1.6);
      stem.position.set(cx, stemH * 0.5, cz);
      g.add(stem);
      const rad = 0.3 + w * 0.2;
      const plate = new THREE.Mesh(decoBlobGeo, mat);
      plate.scale.set(rad, rad * 0.16, rad);
      plate.position.set(cx, stemH + rad * 0.12, cz);
      plate.rotation.y = v * 7;
      plate.rotation.z = (v - 0.5) * 0.18; // plates sit slightly off level
      g.add(plate);
    } else {
      // Finger coral: a tight cluster of stubby tubes
      for (let b = 0; b < 4; b++) {
        const len = 0.16 + ((w * (b + 3) * 11) % 1) * 0.26;
        const t = new THREE.Mesh(coralBranchGeo, mat);
        t.scale.set(1.3, len, 1.3);
        t.position.set(cx + (b - 1.5) * 0.09, len * 0.5, cz + ((b % 2) - 0.5) * 0.13);
        g.add(t);
      }
    }
  }
  g.position.set(pos.x, bedY, pos.z);
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

// Cream and tan clams, a blue-grey akoya, a dark ark shell. The beds are
// what he dives for pearls in, so the vocabulary is the shellfish a Japanese
// coast actually holds: asari and hamaguri clams, akoya oysters, abalone,
// and sazae turban shells.
const shellMats = [0xd6d0c2, 0xc8b795, 0xa9b1b8, 0x8b7a63].map((c) => toonMat(c));
const sazaeGeo = new THREE.ConeGeometry(0.5, 1, 6);
function makeShellfishBed(q: number, r: number, h: number): THREE.Group {
  const g = new THREE.Group();
  const pos = hexToWorld(q, r);
  const bedY = h * HEX_H + HEX_H / 2;
  for (let i = 0; i < 8; i++) {
    const v = hash2(q * 53 + i * 13, r * 29 + i * 17);
    const w = hash2(q * 37 + i * 23, r * 71 + i * 3);
    const mat = shellMats[Math.floor(v * shellMats.length) % shellMats.length];
    const rad = 0.13 + v * 0.11;
    const cx = (v - 0.5) * 1.5, cz = (w - 0.5) * 1.5;
    const form = Math.floor(w * 5) % 5;
    if (form === 0) {
      // Gaping clam, two halves parted — the silhouette that reads as
      // shellfish rather than scattered pebbles.
      ([-1, 1] as const).forEach((side) => {
        const half = new THREE.Mesh(decoBlobGeo, mat);
        half.scale.set(rad, rad * 0.34, rad);
        half.position.set(cx, rad * 0.3, cz + side * rad * 0.28);
        half.rotation.x = side * 0.5;
        g.add(half);
      });
    } else if (form === 1) {
      // Sazae: a squat spiral turban standing proud of the sand
      const sz = new THREE.Mesh(sazaeGeo, mat);
      sz.scale.set(rad * 1.5, rad * 1.6, rad * 1.5);
      sz.position.set(cx, rad * 0.75, cz);
      sz.rotation.y = v * 6;
      sz.rotation.z = (w - 0.5) * 0.5;
      g.add(sz);
    } else if (form === 2) {
      // Abalone/oyster pair lying flat and overlapping, as they cluster
      ([0, 1] as const).forEach((n) => {
        const s = new THREE.Mesh(decoBlobGeo, mat);
        s.scale.set(rad * 1.15, rad * 0.22, rad * 0.85);
        s.position.set(cx + n * rad * 0.6, rad * 0.14, cz + n * rad * 0.3);
        s.rotation.y = v * 6 + n * 1.1;
        g.add(s);
      });
    } else {
      // Closed clam, half-buried and tipped at its own angle
      const shell = new THREE.Mesh(decoBlobGeo, mat);
      shell.scale.set(rad, rad * 0.4, rad * 0.82);
      shell.position.set(cx, rad * 0.14, cz);
      shell.rotation.y = v * 6;
      shell.rotation.z = (w - 0.5) * 0.5;
      g.add(shell);
    }
  }
  g.position.set(pos.x, bedY, pos.z);
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

// ================= Grass ground cover (tufts + wildflowers) =================
// Instanced so a whole meadow's worth of blades/flowers costs one draw call
// each, regardless of count — streamed in/out per-column exactly like
// blocks/trees, but instances are recycled through a free-list instead of
// being created/destroyed (InstancedMesh has a fixed capacity; unused slots
// are just scaled to nothing rather than removed).
class InstancedPool {
  readonly mesh: THREE.InstancedMesh;
  // Optional ink outline: a second instanced pass of the same geometry,
  // back faces only and slightly inflated, whose matrices are kept in step
  // with the main pool's. addOutlines() can't help here — it adds a child
  // mesh per object, and an InstancedMesh has no per-instance children.
  readonly outlineMesh: THREE.InstancedMesh | null = null;
  // Optional per-instance UV slide, for pools whose material was built by
  // tileMat — see its comment for why the terrain needs one.
  private readonly uvOffset: THREE.InstancedBufferAttribute | null = null;
  private readonly inflate = new THREE.Vector3();
  private readonly free: number[] = [];
  private next = 0;
  private readonly max: number;
  private readonly dummy = new THREE.Object3D();
  private readonly zeroMatrix = new THREE.Matrix4().makeScale(0, 0, 0);

  constructor(geo: THREE.BufferGeometry, mat: THREE.Material | THREE.Material[], max: number,
              outlineColor?: number, uvJitter = false) {
    this.max = max;
    if (uvJitter) {
      // Every block pool is handed the same prism geometry, so each needs a
      // copy of its own: one shared attribute buffer would have five pools
      // writing over each other's instance slots. Cloned before the colour
      // seeding below so the clone carries that attribute too.
      geo = geo.clone();
      this.uvOffset = new THREE.InstancedBufferAttribute(new Float32Array(max * 2), 2);
      this.uvOffset.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('instanceUvOffset', this.uvOffset);
    }
    // These materials use vertexColors so that instanceColor reaches the
    // fragment stage, and the shader multiplies vColor by the geometry's
    // `color` attribute. Without one, WebGL supplies (0,0,0) for the
    // missing attribute and every instance renders black no matter what
    // colour it was given — so seed a white attribute for it to multiply.
    if (!geo.getAttribute('color')) {
      const white = new Float32Array(geo.getAttribute('position').count * 3).fill(1);
      geo.setAttribute('color', new THREE.BufferAttribute(white, 3));
    }
    this.mesh = new THREE.InstancedMesh(geo, mat, max);
    // Instances are scattered across the whole loaded area, but the mesh's
    // own transform stays at the origin — per-object frustum culling would
    // test against the tiny local geometry bounds and cull the entire
    // group incorrectly. All instances beyond MAX ever placed are already
    // near-zero cost (zero-scale, ~6-20 tris), so there's nothing to save
    // by culling anyway.
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(max * 3), 3);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < max; i++) this.mesh.setMatrixAt(i, this.zeroMatrix);

    if (outlineColor !== undefined) {
      // Additive per-axis growth, so the ink line is a constant width in
      // world units rather than a percentage — small stones would otherwise
      // get hairlines and big ones slabs.
      geo.computeBoundingBox();
      const size = new THREE.Vector3();
      geo.boundingBox!.getSize(size);
      this.inflate.set(2 * OUTLINE_T / size.x, 2 * OUTLINE_T / size.y, 2 * OUTLINE_T / size.z);
      const om = new THREE.MeshBasicMaterial({ color: outlineColor, side: THREE.BackSide });
      this.outlineMesh = new THREE.InstancedMesh(geo, om, max);
      this.outlineMesh.frustumCulled = false;
      this.outlineMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      for (let i = 0; i < max; i++) this.outlineMesh.setMatrixAt(i, this.zeroMatrix);
    }
  }

  // sy/sz default to `scale`, so uniform callers (tufts, flowers) pass five
  // arguments as before; rocks pass all three to get varied proportions.
  place(x: number, y: number, z: number, rotY: number, scale: number, color: THREE.Color,
        sy = scale, sz = scale): number | null {
    const i = this.free.length ? this.free.pop()! : (this.next < this.max ? this.next++ : -1);
    if (i < 0) return null;
    this.dummy.position.set(x, y, z);
    this.dummy.rotation.set(0, rotY, 0);
    this.dummy.scale.set(scale, sy, sz);
    this.dummy.updateMatrix();
    this.mesh.setMatrixAt(i, this.dummy.matrix);
    this.mesh.setColorAt(i, color);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    if (this.outlineMesh) {
      this.dummy.scale.set(scale + this.inflate.x, sy + this.inflate.y, sz + this.inflate.z);
      this.dummy.updateMatrix();
      this.outlineMesh.setMatrixAt(i, this.dummy.matrix);
      this.outlineMesh.instanceMatrix.needsUpdate = true;
    }
    return i;
  }

  // Slides this instance's texture sample point. A no-op on pools built
  // without uvJitter, so callers don't have to know which is which.
  setUvOffset(i: number, u: number, v: number): void {
    if (!this.uvOffset) return;
    this.uvOffset.setXY(i, u, v);
    this.uvOffset.needsUpdate = true;
  }

  remove(i: number): void {
    this.mesh.setMatrixAt(i, this.zeroMatrix);
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.outlineMesh) {
      this.outlineMesh.setMatrixAt(i, this.zeroMatrix);
      this.outlineMesh.instanceMatrix.needsUpdate = true;
    }
    this.free.push(i);
  }
}

// Capacities are sized against UNLOAD_RADIUS — about a thousand columns can
// be resident at once, and every land one now gets a scatter roll, so these
// are several times what the grass-only cover needed. A pool running dry
// doesn't error, it just silently stops placing, which reads as patches of
// bare ground appearing at the streaming edge.
// Clumps, not blades. Around 250 grass columns can be resident at once at
// up to six clumps each, so this has plenty of headroom — and it is a
// quarter of what the per-blade pool needed while drawing thirty times the
// grass, because the matrix buffer scales with instances and the field
// scales with blades.
const TUFT_MAX = 2500;
const FLOWER_MAX = 900;
const SHRUB_MAX = 1600;
const SHELL_MAX = 500;
const DRIFT_MAX = 400;
// One instance here is a CLUMP of ~28 blades, not a blade — see
// world/grass.ts for why. The recipes below place a handful per hex, so
// the blade count per hex is that handful times 28. Authored in a unit
// disc one unit tall, so an instance's scale reads as (radius, blade
// height, radius) in world units.
const tuftGeo = buildGrassClumpGeometry();
const tuftMat = grassMat();
const flowerGeo = new THREE.IcosahedronGeometry(1, 0);
const flowerMat = toonVertexColorMat();
// One blob pool serves the soft masses that read fine as a lump — meadow
// ferns, dry scrub, lichen crusts. They differ only in colour and how flat
// they're squashed, which per-instance colour and non-uniform scale already
// give us, so they don't each need a pool (or a draw call) of their own.
const shrubGeo = new THREE.IcosahedronGeometry(1, 0);

// Shells and driftwood don't, though: at this size a prop is read entirely
// off its silhouette, and as generic blobs they were just litter. Both get
// a real shape, and both are built at radius/length 1 so the scatter
// recipes set their world size directly rather than through PROP_SCALE.

// A ribbed cockle: a low dome, rippled into radial ridges and drawn out to
// a beak on one side. The ridges have to be geometry, not facets — the toon
// material is smooth-shaded and would shade any implied ones away.
//
// A half-disc fan would be the better scallop, but its cut face is open
// geometry: the camera catching it side-on would look straight through the
// shell. A dome's only opening is its base, which sits on the sand.
const shellGeo = (() => {
  const g = new THREE.SphereGeometry(1, 18, 4, 0, Math.PI * 2, 0, Math.PI / 2);
  const p = g.attributes.position;
  const RIDGES = 7;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const a = Math.atan2(z, x);
    // RIDGES is a whole number, so the ripple closes seamlessly all the way
    // round. The second term stretches the dome toward +x into the beak
    // that stops it reading as just another pebble.
    const k = (1 + 0.11 * Math.cos(RIDGES * a)) * (1 + 0.22 * Math.cos(a));
    p.setXYZ(i, x * k, y * (0.4 + 0.06 * Math.cos(RIDGES * a)), z * k);
  }
  p.needsUpdate = true;
  g.computeVertexNormals();
  return g;
})();

// A bleached spar with one broken stub. Built lying along X so place()'s
// single Y rotation swings it flat around the ground plane — an upright
// cylinder would need a tilt the pool can't express, and would read as a
// fencepost. The stub is what tells you it's a branch and not a pipe.
const driftGeo = (() => {
  const spar = new THREE.CylinderGeometry(0.055, 0.085, 1, 6);
  spar.rotateZ(Math.PI / 2);
  const stub = new THREE.CylinderGeometry(0.028, 0.05, 0.36, 5);
  stub.rotateZ(-1.15); // up and along the spar, not straight out of its side
  stub.translate(0.16, 0.075, 0.01);
  const g = mergeGeometries([spar, stub])!;
  // A slight lie, so a spar never sits perfectly level on the sand.
  g.rotateZ(0.09);
  return g;
})();

const tuftPool = new InstancedPool(tuftGeo, tuftMat, TUFT_MAX);
const flowerPool = new InstancedPool(flowerGeo, flowerMat, FLOWER_MAX);
const shrubPool = new InstancedPool(shrubGeo, toonVertexColorMat(), SHRUB_MAX, 0x40382e);
const shellPool = new InstancedPool(shellGeo, toonVertexColorMat(), SHELL_MAX, 0x4a4034);
const driftPool = new InstancedPool(driftGeo, toonVertexColorMat(), DRIFT_MAX, 0x3a3026);

// Stones, sharing the same instanced pool machinery. A dodecahedron is
// chunkier than the icosahedron the foliage uses, and between non-uniform
// scale, free rotation and a spread of greys no two read alike.
const ROCK_MAX = 2600;
const rockGeo = new THREE.DodecahedronGeometry(1, 0);
// Darker ink than the props use, so stones sit down into the ground rather
// than floating on it.
const rockPool = new InstancedPool(rockGeo, toonVertexColorMat(), ROCK_MAX, 0x2b251e);
// In the same family as the stone terrain wash, a shade under it.
const rockGreys = [0xa39e94, 0xaeaa9f, 0x948e84, 0xb3aea3, 0x9c948a];
const rockColor = new THREE.Color();

// Ripples for the rocks that break the surface, pooled the same way so a
// whole shoreline of them is still one draw call.
// Shares water.ts's ripple material, so updateWaterShimmer's opacity tween
// breathes the rock rings along with the wrecks'.
const ripplePool = new InstancedPool(makeRippleGeometry(1), rippleMat, 400);
// White: this multiplies the shared material's colour, so anything tinted
// here would double up on the tint the material already carries.
const rippleColor = new THREE.Color(0xffffff);

export const groundCoverGroup = new THREE.Group();
groundCoverGroup.add(tuftPool.mesh, flowerPool.mesh, rockPool.mesh, ripplePool.mesh);
groundCoverGroup.add(shrubPool.mesh, shellPool.mesh, driftPool.mesh);
[rockPool, shrubPool, shellPool, driftPool].forEach((p) => {
  if (p.outlineMesh) groundCoverGroup.add(p.outlineMesh);
});

// The terrain's own pools, declared up with the voxel code but built here
// because InstancedPool isn't available until its declaration above.
([0, 1, 2, 5, 6] as const).forEach((t) => {
  const pool = new InstancedPool(geometry, MATERIAL_SETS[t], BLOCK_CAPACITY[t], undefined, true);
  blockPools[t] = pool;
  blockGroup.add(pool.mesh);
});

// How well grass does in a given spot: long, dense and deep green in
// sheltered hollows, cropped and bleached on exposed ground. A slow noise
// field, the same trick the forest patches and the dry sand already use,
// and just as cheap — it's read once when the column loads, never per frame.
function grassLushAt(q: number, r: number): number {
  const { q: seedQ, r: seedR } = getTerrainSeed();
  return valueNoise2D((q + seedQ + 3100) * 0.07, (r + seedR + 3300) * 0.07);
}
// The two ends of that field. Everything between is a lerp, rather than a
// pick from a palette — a hard set of four greens is what made neighbouring
// hexes look sorted into types instead of graded across a hillside.
const GRASS_DRY = new THREE.Color(0x9fac6d);
const GRASS_LUSH = new THREE.Color(0x5b8843);
const flowerColors = [0xf7f2e8, 0xead98a, 0xdfa8c6, 0xbfa6d4]; // white, yellow, pink, lilac
// Tideline wrack — kelp and sea-lettuce drying out on the sand. Dark and
// desaturated so it never gets mistaken for something growing there.
const wrackDarks = [0x5c5a3e, 0x6d6444, 0x4b4d38, 0x7a6f4c];
// The dry patches worked through the grass inland: burnt-off, not green.
const scrubOchres = [0xb9a668, 0xa8945a, 0xc7b884, 0x948450];
// Hardy stuff in the cracks up on the peaks — darker and duller than
// lowland grass, since the light up there is what bleaches everything else.
const alpineGreens = [0x8a9a6e, 0x76875e, 0x9aa87e];
const shrubGreens = [0x5f8a4a, 0x6f9a55, 0x527d43, 0x7aa460];
const lichenGreys = [0xb9bda6, 0xa8ad97, 0xc6c9b4, 0x9aa38c];
// Reuses the shellfish-bed vocabulary so a shell on the tideline and a
// shell on the seabed are recognisably the same thing.
const shellPale = [0xd6d0c2, 0xc8b795, 0xa9b1b8, 0x8b7a63];
const driftGreys = [0xb0a692, 0x9e937f, 0xc0b7a3];
const tuftColor = new THREE.Color();
const flowerColor = new THREE.Color();
const coverColor = new THREE.Color();

// Picks from a palette by a 0..1 roll. The modulo guards the case where the
// hash lands at exactly 1.0 and would index off the end.
function pick(palette: number[], v: number): number {
  return palette[Math.floor(v * palette.length) % palette.length];
}

// Stones on a land hex. Rocky ground gets a proper scattering, grass and
// sand only the occasional one, so the stones read as an outcrop breaking
// through rather than uniform gravel everywhere.
function spawnRocks(q: number, r: number, h: number, topType: number, now: number, record: ColumnRecord): void {
  const t = hash2(q * 313 + 11, r * 197 + 29);
  const stony = topType === 2;
  // Sand sits between the two: shingle and washed-up pebbles are a real
  // beach feature, and with no grass on sand any more the stones are
  // carrying more of the hex.
  const chance = stony ? 0.8 : (topType === 5 ? 0.3 : 0.14);
  if (t > chance || isNearVillageSite(q, r, 2, now)) return;
  const pos = hexToWorld(q, r);
  const y = h * HEX_H + HEX_H / 2;
  const n = stony ? 1 + Math.floor(((t * 29) % 1) * 4) : 1;
  for (let i = 0; i < n; i++) {
    const jv = hash2(q * 149 + i * 61 + 3, r * 211 + i * 37 + 5);
    const jw = hash2(q * 83 + i * 17 + 7, r * 109 + i * 47 + 9);
    const s = (stony ? 0.1 : 0.055) + jv * (stony ? 0.16 : 0.07);
    rockColor.setHex(rockGreys[Math.floor(jv * rockGreys.length) % rockGreys.length]);
    const idx = rockPool.place(
      pos.x + (jv - 0.5) * HEX_R * 1.0, y + s * 0.28, pos.z + (jw - 0.5) * HEX_R * 1.0,
      jv * Math.PI * 2, s, rockColor, s * (0.5 + jw * 0.4), s * (0.7 + jv * 0.5),
    );
    if (idx !== null) (record.rockIdx || (record.rockIdx = [])).push(idx);
  }
}

// Boulders standing in the shallows near shore — the rocky islets and
// half-drowned stones that give a coastline its shape. Deep water is left
// clear so there's nothing to snag on out where he rows.
function spawnShallowRocks(q: number, r: number, h: number, record: ColumnRecord): void {
  const t = hash2(q * 431 + 19, r * 157 + 41);
  if (t > 0.11) return;
  const pos = hexToWorld(q, r);
  const bedY = h * HEX_H + HEX_H / 2;
  const n = 1 + Math.floor(((t * 31) % 1) * 2);
  for (let i = 0; i < n; i++) {
    const jv = hash2(q * 173 + i * 53 + 1, r * 233 + i * 29 + 3);
    const jw = hash2(q * 97 + i * 41 + 5, r * 127 + i * 19 + 7);
    const s = 0.09 + jv * 0.17;
    const rx = pos.x + (jv - 0.5) * HEX_R * 0.9, rz = pos.z + (jw - 0.5) * HEX_R * 0.9;
    const sy = s * (0.7 + jv * 0.6);
    rockColor.setHex(rockGreys[Math.floor(jw * rockGreys.length) % rockGreys.length]);
    const idx = rockPool.place(rx, bedY + s * 0.35, rz, jw * Math.PI * 2, s, rockColor, sy, s * (0.75 + jw * 0.45));
    if (idx !== null) (record.rockIdx || (record.rockIdx = [])).push(idx);
    // Every rock that stands proud of the water gets one — ringing only
    // some of them looks broken, since a bare rock beside a ringed one
    // reads as a bug rather than as variation. Ones still submerged are
    // skipped, where the ripple would have nothing to ripple around.
    // The radius is kept tight instead, so neighbours overlap less.
    if (bedY + s * 0.35 + sy > WATER_SURF_Y + 0.01) {
      const ri = ripplePool.place(rx, WATER_SURF_Y + 0.004, rz, jv * 3, s * 1.45, rippleColor);
      if (ri !== null) (record.rippleIdx || (record.rippleIdx = [])).push(ri);
    }
  }
}

// Ground cover. Every land hex gets a roll — what it is a roll *for*
// depends on what the hex is made of, so beaches, dry inland patches and
// bare peaks each get their own vocabulary instead of only grass hexes
// being dressed and everything else left as flat wash.
//
// None of this is gameplay: it is instanced scatter streamed with the
// column, and nothing else in the game reads it. Village sites stay clear
// so market clearings read as trodden ground.
function spawnGroundCover(q: number, r: number, h: number, topType: number, now: number, record: ColumnRecord): void {
  if (isNearVillageSite(q, r, 3, now)) return;
  if (topType === 0) spawnMeadowCover(q, r, h, record);
  else if (topType === 5) spawnSandCover(q, r, h, record);
  else if (topType === 2) spawnStoneCover(q, r, h, record);
  // topType 6 is seabed, under the waterline — the habitat decorations in
  // genColumn dress that, and land scatter there would sit in the sea.
}

// Scatter helpers. Everything below jitters off a single hash per item, in
// the same idiom the original grass cover used: one roll drives the palette
// pick and the x offset, its seventh multiple the z offset, so a clump does
// not line up along a diagonal.
function coverOffset(base: number, i: number, q: number, r: number): number {
  return hash2(q * 131 + i * 41 + base, r * 197 + i * 59 + base);
}

// Grass hexes: blades, the odd wildflower, and low ferns to give the
// meadow some mass between them. Density varies rather than presence — an
// empty hex in the middle of a meadow reads as a hole, where a thin one
// reads as worn ground.
function spawnMeadowCover(q: number, r: number, h: number, record: ColumnRecord): void {
  const t = hash2(q * 811 + 3, r * 977 + 7);
  const pos = hexToWorld(q, r);
  const y = h * HEX_H + HEX_H / 2;
  const lush = grassLushAt(q, r);
  // Coverage comes from the clump count. Touch devices take one fewer,
  // which is 28 blades cheaper per hex across the whole loaded area.
  const n = (IS_TOUCH ? 2 : 3) + Math.floor(((t * 37) % 1) * (1 + lush * 3));
  for (let i = 0; i < n; i++) {
    const jv = coverOffset(1, i, q, r);
    // Offset plus clump radius has to stay inside the hex's inradius
    // (HEX_R * sqrt(3)/2 = 0.537), or a meadow hex spills grass onto the
    // sand next door. 0.242 + 0.28 = 0.522, and the clump geometry is
    // normalised to radius 1 so that second number means what it says.
    const jx = pos.x + (jv - 0.5) * HEX_R * 0.78;
    const jz = pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.78;
    tuftColor.copy(GRASS_DRY).lerp(GRASS_LUSH, Math.min(1, lush * 0.9 + jv * 0.25));
    // Radius and blade height are set separately, so a clump can spread
    // without its blades getting longer — scaling both together gives you
    // leaves, not grass.
    const cr = 0.20 + jv * 0.08;
    const ch = 0.075 + lush * 0.085 + jv * 0.03;
    const idx = tuftPool.place(jx, y, jz, jv * Math.PI * 2, cr, tuftColor, ch, cr);
    if (idx !== null) (record.tuftIdx || (record.tuftIdx = [])).push(idx);
  }
  if (((t * 53) % 1) < 0.24) {
    const fv = hash2(q * 271 + 13, r * 359 + 17);
    const fx = pos.x + (fv - 0.5) * HEX_R * 0.7;
    const fz = pos.z + (((fv * 5) % 1) - 0.5) * HEX_R * 0.7;
    flowerColor.setHex(pick(flowerColors, fv));
    const idx = flowerPool.place(fx, y + 0.02, fz, fv * Math.PI * 2, (0.06 + fv * 0.04) * PROP_SCALE, flowerColor);
    if (idx !== null) (record.flowerIdx || (record.flowerIdx = [])).push(idx);
  }
  if (((t * 97) % 1) < 0.22) {
    const bn = 1 + Math.floor(((t * 113) % 1) * 2);
    for (let i = 0; i < bn; i++) {
      const jv = coverOffset(23, i, q, r);
      const rad = 0.06 + jv * 0.045;
      coverColor.setHex(pick(shrubGreens, jv));
      const idx = shrubPool.place(
        pos.x + (jv - 0.5) * HEX_R * 0.75, y + rad * 0.5, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.75,
        jv * Math.PI * 2, rad, coverColor, rad * (0.6 + jv * 0.3), rad * (0.8 + jv * 0.4),
      );
      if (idx !== null) (record.shrubIdx || (record.shrubIdx = [])).push(idx);
    }
  }
}

// Sand hexes, in two flavours off the same terrain type: a beach down at
// the waterline gets marram grass, shells and the odd driftwood spar; the
// dry patches worked through the grass inland get burnt-off scrub instead.
// Both stay thinner than the meadow — bare sand showing between the clumps
// is the point, it just is not bare across the whole hex any more.
function spawnSandCover(q: number, r: number, h: number, record: ColumnRecord): void {
  const t = hash2(q * 619 + 5, r * 733 + 11);
  const beach = h <= SEA_LEVEL + 1;
  const pos = hexToWorld(q, r);
  const y = h * HEX_H + HEX_H / 2;
  // No blades of any kind here. Sand stays sand: the marram and dry stalks
  // this used to scatter were the same cone geometry as meadow grass, and
  // however they were tinted they still read as a lawn growing out of the
  // beach. What fills the hex instead is what the tide and the wind leave —
  // wrack, shells, driftwood, shingle.
  if (beach) {
    // Tideline wrack: torn dark clumps, wide and pressed flat. Deliberately
    // lopsided on both horizontal axes, since an even round blob is what
    // would read as a bush again.
    if (((t * 41) % 1) < 0.4) {
      const wn = 1 + Math.floor(((t * 59) % 1) * 3);
      for (let i = 0; i < wn; i++) {
        const jv = coverOffset(3, i, q, r);
        const rad = 0.07 + jv * 0.06;
        coverColor.setHex(pick(wrackDarks, jv));
        const idx = shrubPool.place(
          pos.x + (jv - 0.5) * HEX_R * 0.9, y + rad * 0.08, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.9,
          jv * Math.PI * 2, rad * (0.7 + jv * 0.7), coverColor, rad * 0.16, rad * (1.1 + jv * 0.5),
        );
        if (idx !== null) (record.shrubIdx || (record.shrubIdx = [])).push(idx);
      }
    }
    // Sizes here are world units on a unit-radius/unit-length geometry, and
    // are calibrated against a tree's canopy blob (about 0.13-0.21 across,
    // on a hex 1.24 wide) — that's the smallest thing in the game that
    // still reads as a specific object rather than a speck. The first pass
    // put shells at a third of that and driftwood at a 20:1 needle, which
    // is why neither was identifiable.
    if (((t * 89) % 1) < 0.55) {
      const sn = 1 + Math.floor(((t * 127) % 1) * 4);
      for (let i = 0; i < sn; i++) {
        const jv = coverOffset(29, i, q, r);
        const rad = 0.075 + jv * 0.045; // ~0.19-0.30 across the shell
        coverColor.setHex(pick(shellPale, jv));
        const idx = shellPool.place(
          pos.x + (jv - 0.5) * HEX_R * 0.8, y, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.8,
          jv * Math.PI * 2, rad, coverColor, rad * (0.85 + jv * 0.3), rad,
        );
        if (idx !== null) (record.shellIdx || (record.shellIdx = [])).push(idx);
      }
    }
    if (((t * 67) % 1) < 0.3) {
      const jv = coverOffset(31, 0, q, r);
      const len = 0.3 + jv * 0.22;
      // Scale on a spar whose geometry radius is 0.055-0.085, so this lands
      // a touch thicker than a tree trunk — which is what driftwood is.
      const th = 0.35 + jv * 0.22;
      coverColor.setHex(pick(driftGreys, jv));
      const idx = driftPool.place(
        pos.x + (jv - 0.5) * HEX_R * 0.55, y + 0.05 * th, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.55,
        jv * Math.PI * 2, len, coverColor, th, th,
      );
      if (idx !== null) (record.driftIdx || (record.driftIdx = [])).push(idx);
    }
  } else if (((t * 71) % 1) < 0.5) {
    // Inland dry patches get low burnt-off scrub — a woody mass rather than
    // stalks, for the same reason the beach lost its marram.
    const bn = 1 + Math.floor(((t * 83) % 1) * 2);
    for (let i = 0; i < bn; i++) {
      const jv = coverOffset(37, i, q, r);
      const rad = 0.055 + jv * 0.045;
      coverColor.setHex(pick(scrubOchres, jv));
      const idx = shrubPool.place(
        pos.x + (jv - 0.5) * HEX_R * 0.8, y + rad * 0.45, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.8,
        jv * Math.PI * 2, rad, coverColor, rad * (0.5 + jv * 0.3), rad * (0.8 + jv * 0.4),
      );
      if (idx !== null) (record.shrubIdx || (record.shrubIdx = [])).push(idx);
    }
  }
}

// Peaks. spawnRocks already scatters the scree itself, so this is what
// grows between it: lichen crusts flattened onto the rock, and the hardy
// tufts that get a footing in the cracks.
function spawnStoneCover(q: number, r: number, h: number, record: ColumnRecord): void {
  const t = hash2(q * 541 + 7, r * 641 + 13);
  const pos = hexToWorld(q, r);
  const y = h * HEX_H + HEX_H / 2;
  if (((t * 31) % 1) < 0.6) {
    const ln = 1 + Math.floor(((t * 43) % 1) * 3);
    for (let i = 0; i < ln; i++) {
      const jv = coverOffset(41, i, q, r);
      // Lichen wants width, not bulk — a wide thin stain reads, a small
      // one is indistinguishable from the wash it sits on.
      const rad = 0.09 + jv * 0.09;
      coverColor.setHex(pick(lichenGreys, jv));
      // Almost no height at all — lichen is a stain on the stone, and any
      // real thickness turns it into a cushion of moss instead.
      const idx = shrubPool.place(
        pos.x + (jv - 0.5) * HEX_R * 0.95, y + rad * 0.05, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.95,
        jv * Math.PI * 2, rad, coverColor, rad * 0.1, rad * (0.7 + jv * 0.6),
      );
      if (idx !== null) (record.shrubIdx || (record.shrubIdx = [])).push(idx);
    }
  }
  if (((t * 59) % 1) < 0.5) {
    const tn = 1 + Math.floor(((t * 73) % 1) * 2);
    for (let i = 0; i < tn; i++) {
      const jv = coverOffset(47, i, q, r);
      tuftColor.setHex(pick(alpineGreens, jv));
      // A tight cushion rather than a meadow clump: short, stiff and
      // small across. Nothing grows long in the wind up here.
      const cr = 0.06 + jv * 0.035;
      const ch = 0.05 + jv * 0.045;
      const idx = tuftPool.place(
        pos.x + (jv - 0.5) * HEX_R * 0.8, y, pos.z + (((jv * 7) % 1) - 0.5) * HEX_R * 0.8,
        jv * Math.PI * 2, cr, tuftColor, ch, cr,
      );
      if (idx !== null) (record.tuftIdx || (record.tuftIdx = [])).push(idx);
    }
  }
}

// ================= Villages (rendering + streaming; siting/economy in economy/market.ts) =================
export const villageGroup = new THREE.Group();
const villagePlankMat = toonMat(0x9c8259);
const villageMeshes = new Map<Village, THREE.Group>();

// Roof colours are what carry the "Japanese" read at a distance: charcoal
// kawara tile on the market, thatch on the dwelling, both with eaves that
// overhang well past the walls. The old flat red canopy read as a generic
// fairground stall, so red survives only as cloth — noren and nobori.
// Near-white tint: the map multiplies the base colour, and these textures
// are already authored at the colour the roof should end up, so anything
// darker here just muddies them.
const roofTileMat = toonMat(0xf2f0ea);
roofTileMat.map = texRoofTile();
const roofRidgeMat = toonMat(0x3b3a3e);
export const thatchMat = toonMat(0xf2f0ea);
thatchMat.map = texThatch();
const timberMat = toonMat(0x5b4a38);
const norenMat = toonMat(0xb3453c);
const paperMat = toonMat(0xf0c98a, 0x6e4210); // lantern paper, lit from within
const plasterMat = toonMat(0xe6dfcd);

// A gabled roof: two pitched slabs meeting under a ridge cap. `width` and
// `depth` are the full extents *including* the eave overhang, so both
// should be comfortably larger than the walls beneath.
//
// The pitch is deliberately steep. A shallow roof reads as a generic hut
// almost regardless of texture; the steep slope plus a heavy ridge and a
// deep overhang is what carries the silhouette.
export const ROOF_PITCH = 0.78; // rise per unit of half-depth — about 38°
export function buildRoof(width: number, depth: number, y: number, mat: THREE.Material): THREE.Group {
  const r = new THREE.Group();
  const half = depth / 2;
  const rise = half * ROOF_PITCH;
  const slope = Math.hypot(half, rise);
  const angle = Math.atan2(rise, half);
  ([1, -1] as const).forEach((side) => {
    const slab = new THREE.Mesh(new THREE.BoxGeometry(width, 0.05, slope), mat);
    slab.position.set(0, rise / 2, side * half / 2);
    slab.rotation.x = side * angle;
    r.add(slab);
  });
  const ridge = new THREE.Mesh(new THREE.BoxGeometry(width * 0.98, 0.062, 0.08), roofRidgeMat);
  ridge.position.y = rise + 0.02;
  r.add(ridge);
  r.position.y = y;
  return r;
}

function buildVillageMesh(v: Village): THREE.Group {
  const g = new THREE.Group();
  const baseY = terrainHeightAt(v.q, v.r) * HEX_H + HEX_H / 2;

  // ---- Market stall: posts, tiled roof with deep eaves, counter ----
  const postG = new THREE.CylinderGeometry(0.03, 0.035, 0.7, 5);
  ([[0.3, 0.25], [0.3, -0.25], [-0.3, 0.25], [-0.3, -0.25]] as const).forEach(([ox, oz]) => {
    const p = new THREE.Mesh(postG, timberMat);
    p.position.set(ox, 0.35, oz);
    g.add(p);
  });
  g.add(buildRoof(1.0, 0.86, 0.7, roofTileMat));
  const counter = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.08, 0.3), villagePlankMat);
  counter.position.set(0, 0.32, 0.18);
  g.add(counter);

  // Noren: split curtain hung under the front eave, the signature that this
  // is a shop rather than a shed. Panels leave a gap you'd duck through.
  ([-0.19, 0.0, 0.19] as const).forEach((ox) => {
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.2, 0.012), norenMat);
    panel.position.set(ox, 0.58, 0.33);
    g.add(panel);
  });

  // Crates
  ([[-0.55, 0.15, 0.1], [0.55, -0.1, 0.13]] as const).forEach(([ox, oz, s2]) => {
    const c = new THREE.Mesh(new THREE.BoxGeometry(s2 * 2, s2 * 2, s2 * 2), villagePlankMat);
    c.position.set(ox, s2, oz);
    c.rotation.y = ox;
    g.add(c);
  });

  // ---- Chochin lantern on its post: ribbed paper cylinder, dark caps ----
  const lp = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.025, 0.8, 5), timberMat);
  lp.position.set(0.75, 0.4, -0.4);
  g.add(lp);
  const lantern = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.062, 0.13, 8), paperMat);
  lantern.position.set(0.75, 0.82, -0.4);
  g.add(lantern);
  ([-0.072, 0.072] as const).forEach((dy) => {
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.014, 8), roofRidgeMat);
    cap.position.set(0.75, 0.82 + dy, -0.4);
    g.add(cap);
  });

  // ---- Fish drying rack: two posts, a crossbar, and a hanging catch ----
  const rack = new THREE.Group();
  ([-0.3, 0.3] as const).forEach((ox) => {
    const p = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.026, 0.62, 5), timberMat);
    p.position.set(ox, 0.31, 0);
    rack.add(p);
  });
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.66, 5), timberMat);
  bar.rotation.z = Math.PI / 2;
  bar.position.y = 0.6;
  rack.add(bar);
  for (let i = 0; i < 5; i++) {
    const f = new THREE.Mesh(fishGeo, fishMats[i % fishMats.length]);
    f.scale.setScalar(0.13);
    f.position.set(-0.24 + i * 0.12, 0.5, 0);
    f.rotation.z = Math.PI / 2;
    rack.add(f);
  }
  rack.position.set(0.15, 0, 0.95);
  rack.rotation.y = 0.4;
  g.add(rack);

  // ---- Nobori: tall vertical banner on a pole ----
  const bpole = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.016, 1.15, 5), timberMat);
  bpole.position.set(-0.72, 0.57, 0.5);
  g.add(bpole);
  const banner = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.66, 0.17), norenMat);
  banner.position.set(-0.705, 0.72, 0.585);
  g.add(banner);

  // ---- Dwelling: cream plaster between dark timber, thatched deep eaves ----
  const hut = new THREE.Group();
  const walls = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.46, 0.6), plasterMat);
  walls.position.y = 0.23;
  hut.add(walls);
  // Posts and sill are kept a few mm clear of the wall's top face (y=0.46)
  // rather than flush with it — coplanar faces z-fight and shimmer.
  ([[0.35, 0.3], [0.35, -0.3], [-0.35, 0.3], [-0.35, -0.3]] as const).forEach(([ox, oz]) => {
    const c = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.43, 0.05), timberMat);
    c.position.set(ox, 0.215, oz);
    hut.add(c);
  });
  const sill = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.04, 0.64), timberMat);
  sill.position.y = 0.425;
  hut.add(sill);
  hut.add(buildRoof(1.06, 0.94, 0.46, thatchMat));
  // Engawa: the veranda platform along the front, on short piles
  const engawa = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.05, 0.2), villagePlankMat);
  engawa.position.set(0, 0.1, 0.38);
  hut.add(engawa);
  ([-0.3, 0.3] as const).forEach((ox) => {
    const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.1, 4), timberMat);
    pile.position.set(ox, 0.05, 0.44);
    hut.add(pile);
  });
  hut.position.set(-1.4, 0, -0.9);
  hut.rotation.y = 0.3;
  g.add(hut);

  g.position.set(v.x, baseY, v.z);
  g.rotation.y = hash2(v.q, v.r) * Math.PI * 2;
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

function streamVillageMesh(v: Village): void {
  if (!v.ok) return;
  const d = Math.hypot(v.x - player.x, v.z - player.z);
  const mesh = villageMeshes.get(v);
  if (d < PROP_STREAM_IN && !mesh) {
    const g = buildVillageMesh(v);
    villageMeshes.set(v, g);
    villageGroup.add(g);
  } else if (d > PROP_STREAM_OUT && mesh) {
    villageGroup.remove(mesh);
    villageMeshes.delete(v);
  }
}

// Streams village meshes in/out based on distance; call nearestVillage(...)
// yourself first (main.ts) so nearby regions are computed before this runs.
// extraVillages (world/fixedMap.ts's hand-placed markets) aren't cached in
// `villages` under real region keys, so they need their own pass here.
export function updateVillageMeshes(): void {
  villages.forEach(streamVillageMesh);
  extraVillages.forEach(streamVillageMesh);
}

// ================= Shipwrecks (siting + rendering + streaming) =================
export const wreckGroup = new THREE.Group();
const WRECK_REGION = 30;
export const wrecks = new Map<string, WreckSite>();
const wreckMat = toonMat(0x5b4d3a);
wreckMat.side = THREE.DoubleSide; // the hull shell is open, so it's seen from inside too

function wreckFor(rx: number, rz: number): WreckSite {
  const rk = rx + ',' + rz;
  let wk = wrecks.get(rk);
  if (wk) return wk;
  const exists = hash2(rx * 313 + 11, rz * 197 + 3) < 0.55;
  const aq = Math.round(rx * WRECK_REGION + (hash2(rx * 23 + 1, rz * 41 + 9) - 0.5) * WRECK_REGION * 0.6);
  const ar = Math.round(rz * WRECK_REGION + (hash2(rx * 61 + 17, rz * 19 + 5) - 0.5) * WRECK_REGION * 0.6);
  let site: { q: number; r: number } | null = null;
  if (exists) {
    outer:
    for (let radius = 0; radius < 12; radius++) {
      for (let dq = -radius; dq <= radius; dq++) {
        const rMin = Math.max(-radius, -dq - radius), rMax = Math.min(radius, -dq + radius);
        for (let dr = rMin; dr <= rMax; dr++) {
          if (axialDistance(0, 0, dq, dr) !== radius) continue;
          if (terrainHeightAt(aq + dq, ar + dr) <= SEA_LEVEL - 3) { site = { q: aq + dq, r: ar + dr }; break outer; }
        }
      }
    }
  }
  const wq = site ? site.q : aq, wr = site ? site.r : ar;
  const pos = hexToWorld(wq, wr);
  wk = { ok: !!site, q: wq, r: wr, x: pos.x, z: pos.z, mesh: null, loot: 4 };
  wrecks.set(rk, wk);
  return wk;
}

function buildWreckMesh(wk: WreckSite): THREE.Group {
  const g = new THREE.Group();
  const bedY = terrainHeightAt(wk.q, wk.r) * HEX_H + HEX_H / 2;
  // Children are positioned relative to the group rather than at absolute
  // bedY heights, so the group can carry both the seabed height and
  // PROP_SCALE — the old absolute form couldn't be scaled without dragging
  // the wreck off the bed.
  const hull = new THREE.Mesh(hullGeo, wreckMat);
  hull.scale.set(1.7, 1.7, 1.7);
  hull.rotation.z = 0.45;
  hull.rotation.y = hash2(wk.q, wk.r) * Math.PI * 2;
  hull.position.y = 0.12;
  g.add(hull);
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 1.7, 5), wreckMat);
  mast.position.set(0.15, 0.8, 0.1);
  mast.rotation.z = 0.5;
  mast.rotation.x = 0.12;
  g.add(mast);
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.55, 4), wreckMat);
  beam.rotation.z = Math.PI / 2 + 0.4;
  beam.position.set(0.5, 1.4, 0.18);
  g.add(beam);
  g.position.set(wk.x, bedY, wk.z);
  g.scale.setScalar(PROP_SCALE);
  // Most wrecks lie deep, but the mast is long enough to break the surface
  // on the shallower sites. Ring only those — added outside the scaled
  // group so the ripple keeps its own size and sits flat on the water.
  const mastTopY = bedY + 1.65 * PROP_SCALE;
  if (mastTopY > WATER_SURF_Y + 0.02) {
    const rings = makeRippleRings(0.22);
    rings.position.set(wk.x, WATER_SURF_Y + 0.004, wk.z);
    wreckGroup.add(rings);
    wk.ripple = rings;
  }
  return g;
}

export function updateWrecks(): void {
  const pq = worldToHex(player.x, player.z);
  const rx0 = Math.round(pq.q / WRECK_REGION), rz0 = Math.round(pq.r / WRECK_REGION);
  for (let rx = rx0 - 1; rx <= rx0 + 1; rx++) {
    for (let rz = rz0 - 1; rz <= rz0 + 1; rz++) wreckFor(rx, rz);
  }
  wrecks.forEach((wk) => {
    if (!wk.ok) return;
    const d = Math.hypot(wk.x - player.x, wk.z - player.z);
    if (d < PROP_STREAM_IN && !wk.mesh) { wk.mesh = buildWreckMesh(wk); wreckGroup.add(wk.mesh); }
    else if (d > PROP_STREAM_OUT && wk.mesh) {
      wreckGroup.remove(wk.mesh);
      wk.mesh = null;
      if (wk.ripple) { wreckGroup.remove(wk.ripple); wk.ripple = null; }
    }
  });
}

export function nearbyWreck(x: number, z: number, range: number): WreckSite | null {
  let found: WreckSite | null = null;
  wrecks.forEach((wk) => {
    if (wk.ok && !found && Math.hypot(wk.x - x, wk.z - z) < range) found = wk;
  });
  return found;
}

// ================= Column streaming =================
export const LOAD_RADIUS = IS_TOUCH ? 11 : 14;
const UNLOAD_RADIUS = IS_TOUCH ? 14 : 18;

// Loaded terrain reaches roughly this far in world units — a hex step is
// about 1.07 units, so a hex-radius disc of LOAD_RADIUS ends about here.
// Anything that streams in beyond it hangs over empty space, and fog that
// starts beyond it never hides the boundary, so both are derived from it.
export const LOAD_WORLD = LOAD_RADIUS * 1.07;
// Landmarks come in just inside the terrain edge and leave a little past
// it, so they fade in already fogged rather than popping over nothing.
const PROP_STREAM_IN = LOAD_WORLD * 0.9;
const PROP_STREAM_OUT = LOAD_WORLD * 1.15;

interface ColumnRecord {
  cells: Set<string>;
  treeKey?: string;
  deco?: THREE.Object3D;
  seam?: THREE.Object3D;
  tuftIdx?: number[];
  flowerIdx?: number[];
  shrubIdx?: number[];
  shellIdx?: number[];
  driftIdx?: number[];
  rockIdx?: number[];
  rippleIdx?: number[];
}
const loadedColumns = new Map<string, ColumnRecord>();

// Hexes whose habitat decoration has been cleared — currently where a jetty
// was driven through. Kept as its own set rather than just deleting the
// meshes, because a column that unloads and streams back in would otherwise
// regrow the mangrove straight through the decking.
const decoSuppressed = new Set<string>();

export function clearDecoAt(q: number, r: number): void {
  const k = q + ',' + r;
  decoSuppressed.add(k);
  const rec = loadedColumns.get(k);
  if (rec?.deco) {
    decoGroup.remove(rec.deco);
    rec.deco = undefined;
  }
}

// Lets world/fixedMap.ts force specific hexes to a texture type regardless
// of the normal height-based rule below (its island interior sits at only
// one level above its shoreline — too low to naturally clear the sand
// threshold — but should still read as grass). null (the default) leaves
// every procedural world's texturing untouched.
let topTypeOverride: ((q: number, r: number) => number | null) | null = null;

export function setTopTypeOverride(fn: ((q: number, r: number) => number | null) | null): void {
  topTypeOverride = fn;
}

// How far inland sand is allowed to reach, in hexes.
const SAND_REACH = 3;

// True if any hex within `range` is below sea level. Scans the disc and
// bails on the first hit, so coastal hexes — the common case when this is
// asked — cost only a few terrainHeightAt calls. Only consulted for hexes
// that are already going to be sand, so most columns never run it.
function waterWithin(q: number, r: number, range: number): boolean {
  for (let dq = -range; dq <= range; dq++) {
    const rMin = Math.max(-range, -dq - range), rMax = Math.min(range, -dq + range);
    for (let dr = rMin; dr <= rMax; dr++) {
      if (terrainHeightAt(q + dq, r + dr) < SEA_LEVEL) return true;
    }
  }
  return false;
}

// now: a timestamp threaded through to isNearVillageSite (economy/market.ts),
// which lazily sites villages and needs a clock for their demand-recovery bookkeeping.
// True for hexes that read as sea rather than shore: anything below the
// waterline, plus a shelf sitting exactly at sea level with no dry land
// beside it. That last case is the whole point — such a hex is land as far
// as the rest of the code is concerned (no water tile is drawn on it), but
// it sits ringed by water, and its warm cream seam was what drew beige
// lines across the open sea. A hex at the same height touching real land is
// a beach, and keeps its warm line.
//
// Deliberately only affects the seam colour. Repainting the terrain to
// match is what washed the colour out of the shorelines last time.
function readsAsSea(q: number, r: number, h: number): boolean {
  if (h < SEA_LEVEL) return true;
  if (h !== SEA_LEVEL) return false;
  return !HEX_SIDE_DIRS.some(([dq, dr]) => terrainHeightAt(q + dq, r + dr) > SEA_LEVEL);
}

function genColumn(q: number, r: number, record: ColumnRecord, now: number): void {
  const h = terrainHeightAt(q, r);
  const forcedTopType = topTypeOverride ? topTypeOverride(q, r) : null;
  const { q: seedQ, r: seedR } = getTerrainSeed();
  const atSea = readsAsSea(q, r, h);
  let topType: number;
  if (forcedTopType !== null) {
    topType = forcedTopType; // hand-authored board: leave it alone
  } else {
    if (h <= SEA_LEVEL + 1) topType = 5;
    else if (h >= PEAK_LEVEL) topType = 2;
    else topType = 0;
    // Dry ground worked through the grass in patches, so the land reads as
    // mottled rather than a solid green field. A slow noise field keeps
    // them clustered instead of salt-and-pepper. Ground cover already gates
    // on topType === 0, so tufts and flowers thin out over these naturally.
    if (topType === 0 && valueNoise2D((q + seedQ + 820) * 0.11, (r + seedR + 910) * 0.11) > 0.63) {
      topType = 5;
    }
    // Sand is a beach, not a desert: anything further inland than a few
    // hexes reverts to grass. This catches both sources above — a low flat
    // plain far from the sea, and dry patches rolled in the middle of a
    // landmass — which is what produced the big inland sand fields.
    if (topType === 5 && !waterWithin(q, r, SAND_REACH)) topType = 0;
    // Sand out in the water is seabed, not beach. It's the exposed side
    // wall of these that pulses beige as the swell rises and falls past it.
    if (topType === 5 && atSea) topType = 6;
  }

  // Only the surface block gets a seam. The buried ones were drawing lines
  // nobody can see — six times more of them than were ever visible.
  const cpos = hexToWorld(q, r);
  // Awash hexes take the cool seam too — the cream one is what was drawing
  // beige lines across the water at the shelf's rim.
  const seam = new THREE.LineSegments(atSea ? seamGeoWet : seamGeo, seamMat);
  seam.position.set(cpos.x, h * HEX_H + columnSettle(q, r), cpos.z);
  seam.rotation.y = blockRotY(q, r);
  seamGroup.add(seam);
  record.seam = seam;

  for (let i = 0; i < GEN_DEPTH; i++) {
    const y = h - i;
    // Seabed underneath too, or warm soil sits directly under the cool cap
    // and shows in that same exposed wall.
    const btype = i === 0 ? topType : (i <= 2 ? (atSea ? 6 : 1) : 2);
    const k = key(q, r, y);
    if (!world.has(k)) spawnBlock(q, r, y, btype);
    record.cells.add(k);
  }
  if (h < SEA_LEVEL) {
    addWaterSurface(q, r);
    record.cells.add(key(q, r, SEA_LEVEL));
    const hab = decoSuppressed.has(q + ',' + r) ? null : habitatAt(q, r);
    if (hab === 'mangrove' || hab === 'coral' || hab === 'shellfish') {
      record.deco = hab === 'mangrove' ? makeMangrove(q, r)
        : hab === 'coral' ? makeCoral(q, r, h)
        : makeShellfishBed(q, r, h);
      decoGroup.add(record.deco);
    }
    // Only the shallows — deep water stays clear so nothing lurks out where
    // he rows, and so the rocks read as a shoreline feature.
    if (h >= SEA_LEVEL - 2) spawnShallowRocks(q, r, h, record);
  } else {
    spawnRocks(q, r, h, topType, now, record);
    spawnGroundCover(q, r, h, topType, now, record);
  }
  if (h >= SEA_LEVEL + 2 && h < PEAK_LEVEL) {
    // Forest patches: a slow noise field marks wooded zones where trees
    // grow densely; elsewhere they stay scattered. The ceiling is the
    // fraction of hexes in a forest core that get a tree — one hex holds at
    // most one tree, so 0.72 is close to a solid canopy.
    const forest = valueNoise2D((q + seedQ + 1500) * 0.09, (r + seedR + 1600) * 0.09);
    const chance = Math.min(0.72, 0.09 + Math.max(0, forest - 0.5) * 2.6);
    const t = hash2(q * 131 + 7, r * 151 + 13);
    if (t < chance && !isNearVillageSite(q, r, 4, now)) {
      const v = hash2(q * 977 + 3, r * 613 + 11);
      const treeKey = spawnTree(q, r, h, v);
      if (treeKey) record.treeKey = treeKey;
    }
  }
}

function loadColumn(q: number, r: number, now: number): void {
  const ck = q + ',' + r;
  if (loadedColumns.has(ck)) return;
  const record: ColumnRecord = { cells: new Set() };
  loadedColumns.set(ck, record);
  genColumn(q, r, record, now);
}

function unloadColumn(ck: string): void {
  const record = loadedColumns.get(ck);
  if (!record) return;
  record.cells.forEach((k) => {
    const cell = world.get(k);
    if (!cell) return;
    if (cell.kind === 'water') { blockGroup.remove(cell.mesh); waterMeshes.delete(cell.mesh as THREE.Mesh); }
    else blockPools[cell.type].remove(cell.idx);
    world.delete(k);
  });
  if (record.seam) seamGroup.remove(record.seam);
  if (record.treeKey) removeTreeMesh(record.treeKey);
  if (record.deco) decoGroup.remove(record.deco);
  if (record.tuftIdx) record.tuftIdx.forEach((i) => tuftPool.remove(i));
  if (record.flowerIdx) record.flowerIdx.forEach((i) => flowerPool.remove(i));
  if (record.shrubIdx) record.shrubIdx.forEach((i) => shrubPool.remove(i));
  if (record.shellIdx) record.shellIdx.forEach((i) => shellPool.remove(i));
  if (record.driftIdx) record.driftIdx.forEach((i) => driftPool.remove(i));
  if (record.rockIdx) record.rockIdx.forEach((i) => rockPool.remove(i));
  if (record.rippleIdx) record.rippleIdx.forEach((i) => ripplePool.remove(i));
  loadedColumns.delete(ck);
}

export function loadStartArea(now: number): void {
  const p = worldToHex(player.x, player.z);
  for (let dq = -LOAD_RADIUS; dq <= LOAD_RADIUS; dq++) {
    const rMin = Math.max(-LOAD_RADIUS, -dq - LOAD_RADIUS), rMax = Math.min(LOAD_RADIUS, -dq + LOAD_RADIUS);
    for (let dr = rMin; dr <= rMax; dr++) loadColumn(p.q + dq, p.r + dr, now);
  }
}

let chunkTimer = 0;
export function updateChunks(dt: number, now: number): void {
  chunkTimer += dt;
  if (chunkTimer < 0.35) return;
  chunkTimer = 0;
  const p = worldToHex(player.x, player.z);
  for (let dq = -LOAD_RADIUS; dq <= LOAD_RADIUS; dq++) {
    const rMin = Math.max(-LOAD_RADIUS, -dq - LOAD_RADIUS), rMax = Math.min(LOAD_RADIUS, -dq + LOAD_RADIUS);
    for (let dr = rMin; dr <= rMax; dr++) loadColumn(p.q + dq, p.r + dr, now);
  }
  Array.from(loadedColumns.keys()).forEach((ck) => {
    const parts = ck.split(',');
    const q = parseInt(parts[0], 10), r = parseInt(parts[1], 10);
    if (axialDistance(q, r, p.q, p.r) > UNLOAD_RADIUS) unloadColumn(ck);
  });
}
